import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {IconButton} from '@mui/material';
import {Close} from '@mui/icons-material';
import {useChatbotActivities} from '@assets/hooks/useChatbotActivities';
import {
  addCampaignPopupShowed,
  addNewsletterPopupShowed,
  addCampaignMessages,
  addWelcomePopupShowed,
  checkShowNewsletterPopup,
  checkShowWelcomePopup
} from '@scripttag/helpers/storage';
import WidgetWelcomePopup from '@assets/components/Preview/WidgetWelcomePopup';
import WidgetPopupV2 from './WidgetPopupV2';
import {CAMPAIGN_TEMPLATE_ID, CAMPAIGN_TYPE_DRIVEN} from '@assets/const/campaignTemplate';
import {DEFAULT_MESSAGE_TYPE} from '@functions/helpers/defaultCampaignSetting';
import {useWidgetLabelChanged} from '@assets/pages/Widget/WidgetLabelChangedProvider';
import {cleanString} from '@assets/helpers/blockXSSAttack';

const WelcomePopupContainer = ({
  settings,
  adminClass,
  previewPage,
  openWelcomePopup,
  setOpenWelcomePopup,
  openNewsletterPopup,
  setOpenNewsletterPopup,
  bottomPopup,
  isPreview,
  nextPage,
  setCurrentTab,
  open,
  setOpen,
  putData,
  wizard,
  activeCampaigns,
  setActiveCampaigns,
  setShowInput,
  i18n,
  i18nCampaign,
  isFromCampaignPreview
}) => {
  const [isShowPopup, setIsShowPopup] = useState(false);
  const {
    floatBtnPosition,
    showNavigation: isShowNavigation,
    isEnableNewsletterPopup,
    isLiveChat,
    newsletterPopupSettings,
    welcomePopupSetting
  } = settings;
  const {addActivity} = useChatbotActivities(isPreview, putData);
  const {isLabelChanged} = useWidgetLabelChanged();

  const [isWelcomePopup, isNewsletterPopup, isProductRecommendationPopup, isCartBoosterPopup] = [
    'welcome-popup',
    'newsletter-popup',
    'product-recommendation-popup',
    'cart-booster-popup',
    'chat-availability'
  ].map(x => x === previewPage);

  const handleClosePopups = e => {
    e.stopPropagation();
    openWelcomePopup && addWelcomePopupShowed();
    openNewsletterPopup && addNewsletterPopupShowed();
    setOpenWelcomePopup(false);
    setOpenNewsletterPopup(false);
  };

  const handleGoToChat = () => {
    const canOpenChat = isLiveChat || isEnableNewsletterPopup || isPreview;
    nextPage(canOpenChat ? 'chatDetail' : 'homePage');
    setCurrentTab(canOpenChat && isShowNavigation ? 1 : 0);
  };
  const isShowWelcomePopup = useMemo(
    () => isWelcomePopup || (!isPreview && openWelcomePopup && checkShowWelcomePopup()),
    [openWelcomePopup]
  );

  const isShowNewsletterPopup = useMemo(
    () => isNewsletterPopup || (!isPreview && openNewsletterPopup && checkShowNewsletterPopup()),
    [openNewsletterPopup]
  );

  const checkShowCloseButton = () => {
    switch (previewPage) {
      case 'welcome-popup':
        return welcomePopupSetting.statusTeaser;
      case 'newsletter-popup':
        return newsletterPopupSettings.statusTeaser;
      default:
        return false;
    }
  };

  const handleClickProductPopup = productRecommendationMessages => {
    addCampaignMessages(productRecommendationMessages);
    addCampaignPopupShowed(
      activeCampaigns
        .map(x => x.templateId === CAMPAIGN_TEMPLATE_ID.PRODUCT_RECOMMENDATION && x.id)
        .filter(Boolean)
    );
    setActiveCampaigns(
      activeCampaigns.filter(x => x.templateId !== CAMPAIGN_TEMPLATE_ID.PRODUCT_RECOMMENDATION)
    );
    setOpen(true);
    handleGoToChat();
    addActivity('welcomeVisitorEngaged').then();
  };

  const handleClickCartBoosterPopup = (campaign, productRecommendationMessages) => {
    let cartBoosterMessages = [
      {
        id: 'cart-booster-question',
        message: isLabelChanged
          ? cleanString(campaign?.message?.content)
          : i18nCampaign('message', campaign?.id),
        isAdmin: true,
        createdAt: new Date()
      }
    ];
    if (campaign?.message?.type === DEFAULT_MESSAGE_TYPE.DISCOUNT) {
      const {title, shareableUrl} = campaign?.message?.discount ?? {};
      cartBoosterMessages = [
        {
          id: 'cart-booster-discount-welcome',
          message: isLabelChanged
            ? cleanString(campaign?.message?.discountContent)
            : i18nCampaign('discountMessage', campaign?.id),
          isAdmin: true,
          createdAt: new Date()
        },
        {
          id: 'cart-booster-discount-code',
          message: title,
          isCodeMessage: true,
          shareableUrl,
          isAdmin: true,
          createdAt: new Date()
        },
        {
          id: 'cart-booster-discount-usage-instruction',
          message: isLabelChanged
            ? cleanString(campaign?.message?.usageInstruction)
            : i18nCampaign('usageInstruction', campaign?.id),
          isAdmin: true,
          createdAt: new Date()
        }
      ];
    } else if (campaign?.message?.type === DEFAULT_MESSAGE_TYPE.PRODUCT_RECOMMENDATION) {
      cartBoosterMessages = productRecommendationMessages;
    }
    addCampaignMessages(cartBoosterMessages);
    addCampaignPopupShowed(
      activeCampaigns
        .map(x => x.templateId === CAMPAIGN_TEMPLATE_ID.CART_BOOSTER && x.id)
        .filter(Boolean)
    );
    setActiveCampaigns(
      activeCampaigns.filter(x => x.templateId !== CAMPAIGN_TEMPLATE_ID.CART_BOOSTER)
    );
  };

  const handleClickBrowserCollectionPagePopup = (campaign, productRecommendationMessages) => {
    let browserCollectionMessages = [
      {
        id: 'browser-collection-question',
        message: isLabelChanged
          ? cleanString(campaign?.message?.content)
          : i18nCampaign('message', campaign?.id),
        isAdmin: true,
        createdAt: new Date()
      }
    ];
    if (campaign?.message?.type === DEFAULT_MESSAGE_TYPE.DISCOUNT) {
      const {title, shareableUrl} = campaign?.message?.discount ?? {};
      browserCollectionMessages = [
        {
          id: 'browser-collection-discount-welcome',
          message: isLabelChanged
            ? cleanString(campaign?.message?.discountContent)
            : i18nCampaign('discountMessage', campaign?.id),
          isAdmin: true,
          createdAt: new Date()
        },
        {
          id: 'browser-collection-discount-code',
          message: title,
          isCodeMessage: true,
          shareableUrl,
          isAdmin: true,
          createdAt: new Date()
        },
        {
          id: 'browser-collection-discount-usage-instruction',
          message: isLabelChanged
            ? cleanString(campaign?.message?.usageInstruction)
            : i18nCampaign('usageInstruction', campaign?.id),
          isAdmin: true,
          createdAt: new Date()
        }
      ];
    } else if (campaign?.message?.type === DEFAULT_MESSAGE_TYPE.PRODUCT_RECOMMENDATION) {
      browserCollectionMessages = productRecommendationMessages;
    }
    addCampaignMessages(browserCollectionMessages);
    addCampaignPopupShowed(
      activeCampaigns
        .map(x => x.templateId === CAMPAIGN_TEMPLATE_ID.BROWSER_COLLECTION_PAGE && x.id)
        .filter(Boolean)
    );
    setActiveCampaigns(
      activeCampaigns.filter(x => x.templateId !== CAMPAIGN_TEMPLATE_ID.BROWSER_COLLECTION_PAGE)
    );
  };

  const handleClickPopupV2 = (campaign, product = null) => {
    setOpen(true);
    handleGoToChat();
    addActivity('welcomeVisitorEngaged').then();
    const productRecommendationMessages = product
      ? [
          {
            id: 'product-recommendation-question',
            message: i18n('Hi! Need help with this product?'),
            isAdmin: true,
            createdAt: new Date()
          },
          {
            id: 'product-recommendation-show',
            type: 'product-recommendation-campaign-message',
            message: product?.name || 'Product recommendation message',
            productCampaign: product,
            isAdmin: true,
            createdAt: new Date()
          }
        ]
      : [];
    switch (campaign?.templateId) {
      case CAMPAIGN_TEMPLATE_ID.PRODUCT_RECOMMENDATION:
        if (!isShowPopup) {
          if (isPreview) {
            setIsShowPopup(true);
            setShowInput(false);
          }
          handleClickProductPopup(productRecommendationMessages);
        }
        break;
      case CAMPAIGN_TEMPLATE_ID.CART_BOOSTER:
        if (!isShowPopup) {
          if (isPreview) {
            setIsShowPopup(true);
            setShowInput(false);
          }
          handleClickCartBoosterPopup(campaign, productRecommendationMessages);
        }
        break;
      case CAMPAIGN_TEMPLATE_ID.BROWSER_COLLECTION_PAGE:
        if (!isShowPopup) {
          if (isPreview) {
            setIsShowPopup(true);
            setShowInput(false);
          }
          handleClickBrowserCollectionPagePopup(campaign, productRecommendationMessages);
        }
        break;
      default:
        setActiveCampaigns(activeCampaigns.filter(x => CAMPAIGN_TYPE_DRIVEN[x.templateId]));
        addCampaignPopupShowed(
          activeCampaigns.map(x => !CAMPAIGN_TYPE_DRIVEN[x.templateId] && x.id).filter(Boolean)
        );
        break;
    }
  };

  // ---------------------------- Old Automation Handling Logic ----------------------------
  if (activeCampaigns.length === 0) {
    return (
      <div
        className={`Avada-FaqsWelcomePopup ${adminClass}`}
        style={
          isWelcomePopup ||
          openWelcomePopup ||
          openNewsletterPopup ||
          isNewsletterPopup ||
          isCartBoosterPopup ||
          isProductRecommendationPopup
            ? {bottom: bottomPopup}
            : {}
        }
      >
        {(isWelcomePopup ||
          isNewsletterPopup ||
          isProductRecommendationPopup ||
          isCartBoosterPopup ||
          (!isPreview && (openWelcomePopup || openNewsletterPopup))) &&
          !open && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: floatBtnPosition === 'bottom_right' ? 'end' : 'start',
                gap: 6
              }}
            >
              {(isPreview
                ? checkShowCloseButton()
                : (isShowNewsletterPopup && newsletterPopupSettings.statusTeaser) ||
                  (isShowWelcomePopup && welcomePopupSetting.statusTeaser) ||
                  isCartBoosterPopup ||
                  isProductRecommendationPopup) && (
                <IconButton
                  onClick={handleClosePopups}
                  style={{
                    width: '20px',
                    height: '20px',
                    background: '#ffffff',
                    borderRadius: 6,
                    boxShadow: `0px -1px 0px 0px #B5B5B5 inset, -1px 0px 0px 0px #E3E3E3 inset, 1px 0px 0px 0px #E3E3E3 inset, 0px 1px 0px 0px #E3E3E3 inset`
                  }}
                  aria-label="Close popup"
                  aria-labelledby="close-button-popup"
                >
                  <Close />
                </IconButton>
              )}
              {isShowWelcomePopup && welcomePopupSetting.statusTeaser && (
                <WidgetWelcomePopup
                  {...{settings, wizard, addActivity}}
                  handleClick={() => {
                    setOpenWelcomePopup(false);
                    setOpen(true);
                    handleGoToChat();
                    addActivity('welcomeVisitorEngaged').then();
                  }}
                />
              )}
              {isShowNewsletterPopup && newsletterPopupSettings.statusTeaser && (
                <WidgetWelcomePopup
                  {...{settings, isShowNewsletterPopup, wizard, addActivity}}
                  handleClick={() => {
                    setOpenNewsletterPopup(false);
                    setOpen(true);
                    handleGoToChat();
                  }}
                />
              )}
            </div>
          )}
      </div>
    );
  }

  // ---------------------------- Campaign Handling Logic ----------------------------

  // If is preview but not from campaign preview, don't show the popup
  if (!isFromCampaignPreview && isPreview) return null;
  return (
    <div
      className={`Avada-FaqsWelcomePopup ${adminClass}`}
      style={activeCampaigns.length > 0 ? {bottom: bottomPopup} : {}}
    >
      {!open && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: floatBtnPosition === 'bottom_right' ? 'end' : 'start',
            gap: 6
          }}
        >
          {activeCampaigns.map(campaign => (
            <WidgetPopupV2
              key={campaign.id}
              campaign={campaign}
              floatBtnPosition={floatBtnPosition}
              handleClick={handleClickPopupV2}
              handleClosePopups={e => {
                e.stopPropagation();
                addCampaignPopupShowed([campaign.id]);
                setActiveCampaigns(campaigns =>
                  campaigns ? campaigns.filter(c => c.id !== campaign.id) : []
                );
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

WelcomePopupContainer.propTypes = {
  settings: PropTypes.object,
  adminClass: PropTypes.string,
  previewPage: PropTypes.string,
  openWelcomePopup: PropTypes.bool,
  setOpenWelcomePopup: PropTypes.func,
  bottomPopup: PropTypes.string,
  isPreview: PropTypes.bool,
  nextPage: PropTypes.func,
  setCurrentTab: PropTypes.func,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  putData: PropTypes.func
};

export default WelcomePopupContainer;
